import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { ApiError, api } from '../api/client';
import type { PluginConfigurationError, PluginConfigurationValues, PluginConfigurationView } from '../api/types';
import { useApi } from '../api/useApi';
import { useAuth } from '../auth/AuthContext';
import AsyncSection from '../components/AsyncSection';
import PluginConfigurationForm from '../components/plugins/PluginConfigurationForm';
import PageHeader from '../components/PageHeader';

/// A plugin's settings page, generated from the schema the plugin declared. Every plugin gets
/// one and none of them ship a page of their own.
export default function PluginConfigurationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pluginId = '' } = useParams();

  const configuration = useApi(['plugins', pluginId, 'configuration'], () => api.pluginConfiguration(pluginId));

  // A plugin that brings live television is set up here and put to use under Live TV, where its
  // tuners, its channel scan and its channels sit next to every other source's. The way there is
  // offered from here, because somebody who just added a device is about to want it.
  const { can } = useAuth();
  const managesLiveTv = can('ManageLiveTv');
  const providers = useApi(['livetv', 'providers'], api.liveTvProviders, { enabled: managesLiveTv });
  const bringsLiveTv = providers.data?.some((provider) => provider.providerId === pluginId) ?? false;

  // The edits, tied to the response they started from. When a reload brings a new response
  // the draft is dropped with it, which is why seeding the form needs no effect.
  const [draft, setDraft] = useState<{ source: PluginConfigurationView; values: PluginConfigurationValues }>();
  const [errors, setErrors] = useState<PluginConfigurationError[]>([]);
  const [failure, setFailure] = useState<string>();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const view = configuration.data;
  const values = draft !== undefined && draft.source === view ? draft.values : (view?.values ?? {});

  const save = async () => {
    setSaving(true);
    setFailure(undefined);
    setErrors([]);

    try {
      await api.savePluginConfiguration(pluginId, forSubmission(values));
      setSaved(true);
      configuration.reload();
    } catch (caught) {
      // Per-field failures go onto their fields; anything else is shown once, at the top.
      if (caught instanceof ApiError && caught.errors && caught.errors.length > 0) {
        setErrors(caught.errors);
      } else {
        setFailure(caught instanceof Error ? caught.message : String(caught));
      }
    } finally {
      setSaving(false);
    }
  };

  // The identifier says who published it. The name is what the plugin is called, and what
  // somebody who came here to configure it is looking for.
  const name = view?.name ?? pluginId;

  return (
    <Stack spacing={3}>
      <PageHeader title={name} onBack={() => navigate('/plugins')} />

      <AsyncSection loading={configuration.loading} error={configuration.error} onRetry={configuration.reload}>
        {view && (
          <Stack spacing={3}>
            {failure && <Alert severity="error">{failure}</Alert>}
            {errors.length > 0 && <Alert severity="error">{t('plugins.configuration.invalid')}</Alert>}
            {saved && errors.length === 0 && <Alert severity="success">{t('plugins.configuration.saved')}</Alert>}

            <PluginConfigurationForm
              pluginId={view.pluginId}
              schema={view.schema}
              values={values}
              secretsWithValue={view.secretsWithValue}
              errors={errors}
              disabled={saving}
              onChange={(next) => {
                setDraft({ source: view, values: next });
                setSaved(false);
              }}
            />

            <Box>
              <Button variant="contained" disabled={saving} onClick={() => void save()}>
                {t('common.save')}
              </Button>
            </Box>

            {managesLiveTv && bringsLiveTv && (
              <Alert
                severity="info"
                action={
                  <Button color="inherit" size="small" onClick={() => navigate('/livetv/channels/sources')}>
                    {t('livetvChannels.tab.sources')}
                  </Button>
                }
              >
                {t('plugins.configuration.liveTvHint', {
                  page: t('nav.liveTvChannels'),
                  tab: t('livetvChannels.tab.sources'),
                })}
              </Alert>
            )}
          </Stack>
        )}
      </AsyncSection>
    </Stack>
  );
}

/// Prepares the document for the server. A value the form never reported is left out
/// entirely, which is how a stored secret survives a save that did not touch it - and why an
/// emptied secret, which the form does report, is what removes one.
function forSubmission(values: PluginConfigurationValues): PluginConfigurationValues {
  const document: PluginConfigurationValues = {};

  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === null) {
      continue;
    }

    document[key] = value;
  }

  return document;
}
